import React, { useMemo, useCallback, HTMLAttributes } from 'react';

import Cookies from 'js-cookie';
import { unmount } from '../../../../../services/reactRoot';
import { FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';

import { COOKIE_AST_TOKEN_JS, KEYS } from '../../../../../constants/Common';


import Url from '../../../../../services/Url';

import connectAuth, {
    IAuthMapStateToProps,
    IConnectedProps
} from '../hooks/connectAuth';

import InputIcon from '../../Common/Form/Input/InputIcon';
import { Steps } from '../Constants';
import { actionReturnStep, multipleActions } from '../Store/ContainerReducer';
import { IAuthState } from '../Store/Reducers';
import { Types } from '../Store/SignUpReducer';

interface IAuthTitle {
    step: string;
    uuid?: string;
    theme: string;
    listenTo?: string;
    titleLogin: string;
    initialStep: string;
    isCloseable: boolean;
    titleBirthData: string;
    titlePersonalData: string;
    showAstrolinkLogo: boolean;
}

type TAuthTitle = IConnectedProps<IAuthTitle>;

interface IIcon {
    onClick: React.MouseEventHandler<HTMLSpanElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLSpanElement>;
}

interface ITitle extends HTMLAttributes<HTMLHeadingElement> {
    isSynastryInvite: boolean;
}

const TitleIcon = styled(InputIcon)`
  z-index: 1;
`;

const Close: React.FC<IIcon> = ({ onClick, onKeyDown }) => (
    <TitleIcon
        width={16}
        height={16}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
    >
        <path
            d="M16 1.6 14.4 0 8 6.4 1.6 0 0 1.6 6.4 8 0 14.4 1.6 16 8 9.6l6.4 6.4 1.6-1.6L9.6 8z"
            fillRule="evenodd"
        />
    </TitleIcon>
);

const ArrowBack: React.FC<IIcon> = ({ onClick, onKeyDown }) => (
    <TitleIcon
        width={11}
        height={17}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
    >
        <path
            d="M11 1.983 8.919 0 0 8.5 8.919 17 11 15.017 4.162 8.5z"
            fillRule="evenodd"
        />
    </TitleIcon>
);

const Title = styled.h3<ITitle>`
  width: 100%;
  display: flex;
  position: relative;
  text-align: center;
  font-weight: normal;
  align-items: center;
  margin: 0.4em 0 1em;
  box-sizing: border-box;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.cat};
  height: ${({ isSynastryInvite }) => isSynastryInvite && '5rem'};
  line-height: ${({ isSynastryInvite }) => isSynastryInvite && 1.5};
  max-width: ${({ isSynastryInvite }) => isSynastryInvite && '23rem'};

`;

const AstrolinkIcon = styled.img`
  flex: 1;
  max-height: 35px;
`;

const AuthTitle: React.FC<TAuthTitle> = ({ intl, dispatch, ...props }) => {
    const eventClose = new CustomEvent('ClickCloseAuth');

    const {
        step,
        uuid,
        theme,
        listenTo,
        titleLogin,
        isCloseable,
        initialStep,
        titleBirthData,
        titlePersonalData,
        showAstrolinkLogo
    } = props;

    const isSynastryInvite = theme === 'SynastryInvite';

    const synastryTitle =
        step === Steps.PERSONAL_DATA
            ? 'synastry.invite.authFunctional.titles.personalData'
            : 'synastry.invite.authFunctional.titles.birthData';

    const defaultTitle = intl.formatMessage({ id: 'authFunctional.title' });

    const titleMessage = useMemo(() => {
        if (step === Steps.LOGIN && titleLogin) {
            return titleLogin;
        }

        if (step === Steps.BIRTH_DATA && titleBirthData) {
            return titleBirthData;
        }

        if (step === Steps.PERSONAL_DATA && titlePersonalData) {
            return titlePersonalData;
        }

        return defaultTitle;
    }, [step, defaultTitle, titleBirthData, titleLogin, titlePersonalData]);

    const handleClickReturnStep = useCallback(() => {
        actionReturnStep(dispatch);

        multipleActions(dispatch, [
            {
                type: Types.CHANGE_HOUR_DATA,
                data: { openTimePicker: false }
            },
            {
                type: Types.CHANGE_DATE_DATA,
                data: { openDatePicker: false }
            }
        ]);
    }, [dispatch]);

    const handleKeyDownReturnStep = useCallback(
        ({ key }: { key: string }) => {
            if (key !== KEYS.ENTER) return;

            handleClickReturnStep();
        },
        [handleClickReturnStep]
    );

    const handleClickClose = useCallback(() => {
        document.dispatchEvent(eventClose);

        if (!uuid) return;

        const element = document.querySelector(`[data-uuid="${uuid}"]`);

        if (!element) return;

        unmount(element);

        if (!listenTo) return;

        document.closeStyledModal[listenTo]();
    }, [uuid, listenTo]);

    const titleIcon = useMemo(() => {
        const astToken = Cookies.get(COOKIE_AST_TOKEN_JS);
        const canBeClosed = isCloseable || listenTo !== undefined;

        if ((step === Steps.BIRTH_DATA && !canBeClosed) || isSynastryInvite)
            return null;

        if (step === initialStep && canBeClosed) {
            return <Close onClick={handleClickClose} onKeyDown={handleClickClose} />;
        }

        if (astToken) return;

        return (
            <ArrowBack
                onClick={handleClickReturnStep}
                onKeyDown={handleKeyDownReturnStep}
            />
        );
    }, [
        step,
        listenTo,
        initialStep,
        isCloseable,
        isSynastryInvite,
        handleClickClose,
        handleClickReturnStep,
        handleKeyDownReturnStep
    ]);

    const titleContent = useMemo(() => {
        if (!showAstrolinkLogo)
            return <span dangerouslySetInnerHTML={{ __html: titleMessage }} />;

        if (isSynastryInvite) return <FormattedHTMLMessage id={synastryTitle} />;

        const logoPathPrefix = theme === 'dark' ? '-white' : '';
        const logoPath = Url.image(`auth-functional/logo${logoPathPrefix}.svg`);

        return (
            <AstrolinkIcon
                src={logoPath}
                alt={intl.formatMessage({ id: 'authFunctional.titleAstrolinkLogo' })}
            />
        );
    }, [
        intl,
        theme,
        titleMessage,
        showAstrolinkLogo,
        isSynastryInvite,
        synastryTitle
    ]);

    return (
        <Title isSynastryInvite={isSynastryInvite}>
            {titleIcon}
            {titleContent}
        </Title>
    );
};

const mapStateToProps: IAuthMapStateToProps<IAuthTitle> = (
    state,
    isComparison = false
) =>
    !isComparison
        ? {
            step: (state as IAuthState).Container.step,
            uuid: (state as IAuthState).Container.uuid,
            theme: (state as IAuthState).Container.theme,
            listenTo: (state as IAuthState).Container.listenTo,
            initialStep: (state as IAuthState).Container.initialStep,
            isCloseable: (state as IAuthState).Container.isCloseable,
            showAstrolinkLogo: (state as IAuthState).Container.showAstrolinkLogo,
            titleLogin: (state as IAuthState).Container.communication.titleLogin,
            titleBirthData: (state as IAuthState).Container.communication
                .titleBirthData,
            titlePersonalData: (state as IAuthState).Container.communication
                .titlePersonalData
        }
        : {
            step: state.step,
            uuid: state.uuid,
            theme: state.theme,
            listenTo: state.listenTo,
            titleLogin: state.titleLogin,
            isCloseable: state.isCloseable,
            initialStep: state.initialStep,
            titleBirthData: state.titleBirthData,
            showAstrolinkLogo: state.showAstrolinkLogo,
            titlePersonalData: state.titlePersonalData
        };

export default connectAuth(mapStateToProps)(AuthTitle);
